/**
 * Role membership vocabulary (NEX-2514 member-view slice).
 *
 * Frontend-safe: zero Prisma runtime dependency. Mirrors the
 * `RoleResourceAccessRequestStatus` and `RoleDeletionRequestStatus` Prisma enums
 * by value (uppercase strings) so domain entities never import `$Enums` — same
 * reasoning as `RoleResourceType` in `role.types.ts`, applied to real Prisma
 * enums instead of a TEXT column. `RoleMemberTier` has no database enum behind
 * it: it is the wire name of the `maintainer` (ADMIN) or `member` (MEMBER)
 * system seat a person holds.
 *
 * Each `export const X` is paired with an `export type X` of the same name —
 * the const is the runtime source of truth, the type its compile-time
 * projection. TypeScript merges the two by design (`no-redeclare` is off for
 * this package precisely so that idiom compiles clean).
 */
import { z } from "zod";

/**
 * A user's ADMIN or MEMBER standing inside a Role: a seat in the Role's
 * `maintainer` (ADMIN) or `member` (MEMBER) permission set, which is where every
 * read takes it from and the only place it is stored. OWNER is deliberately absent —
 * `Role.ownerUserId` is the sole source of truth for ownership. See
 * `RoleMembershipTier` for the read-side superset that includes it.
 */
export const RoleMemberTier = {
  ADMIN: "ADMIN",
  MEMBER: "MEMBER"
} as const;

export type RoleMemberTier = (typeof RoleMemberTier)[keyof typeof RoleMemberTier];

export const ROLE_MEMBER_TIERS = Object.values(RoleMemberTier) as [
  RoleMemberTier,
  ...RoleMemberTier[]
];

export const RoleMemberTierSchema = z.enum(ROLE_MEMBER_TIERS);

/**
 * The full tier a person can hold on a Role, for READS that compose
 * `Role.ownerUserId` with the Role's `maintainer`/`member` seats (e.g. "list
 * members", "list my roles"). Never a seat — OWNER always comes from
 * `Role.ownerUserId`.
 */
export const RoleMembershipTier = {
  OWNER: "OWNER",
  ADMIN: "ADMIN",
  MEMBER: "MEMBER"
} as const;

export type RoleMembershipTier = (typeof RoleMembershipTier)[keyof typeof RoleMembershipTier];

export const ROLE_MEMBERSHIP_TIERS = Object.values(RoleMembershipTier) as [
  RoleMembershipTier,
  ...RoleMembershipTier[]
];

export const RoleMembershipTierSchema = z.enum(ROLE_MEMBERSHIP_TIERS);

/** Lifecycle of a `RoleResourceAccessRequest`. REJECTED, not DENIED — matches PhoneNumberRequestStatus. */
export const RoleResourceAccessRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
} as const;

export type RoleResourceAccessRequestStatus =
  (typeof RoleResourceAccessRequestStatus)[keyof typeof RoleResourceAccessRequestStatus];

export const ROLE_RESOURCE_ACCESS_REQUEST_STATUSES = Object.values(
  RoleResourceAccessRequestStatus
) as [RoleResourceAccessRequestStatus, ...RoleResourceAccessRequestStatus[]];

export const RoleResourceAccessRequestStatusSchema = z.enum(ROLE_RESOURCE_ACCESS_REQUEST_STATUSES);

/**
 * Lifecycle of a `RoleDeletionRequest` — the review outcomes of an access
 * request, plus the one ending only a deletion request can have.
 *
 * `SUPERSEDED`: the Role was deleted through another door — an admin's direct
 * delete, or the deletion wizard — while this request was still open. Nobody
 * reviewed it, so it carries no reviewer; the request survives as the record that
 * it was filed. Every Role delete writes it in the same transaction as the delete,
 * and the database refuses a Role delete that would leave a PENDING request
 * behind, so a PENDING request always names a Role that exists.
 *
 * Its own vocabulary rather than a fourth `RoleResourceAccessRequestStatus`
 * value: an access request and a creation request cannot be superseded, and a
 * shared enum would advertise a state their contracts can never produce.
 */
export const RoleDeletionRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  SUPERSEDED: "SUPERSEDED"
} as const;

export type RoleDeletionRequestStatus =
  (typeof RoleDeletionRequestStatus)[keyof typeof RoleDeletionRequestStatus];

export const ROLE_DELETION_REQUEST_STATUSES = Object.values(RoleDeletionRequestStatus) as [
  RoleDeletionRequestStatus,
  ...RoleDeletionRequestStatus[]
];

export const RoleDeletionRequestStatusSchema = z.enum(ROLE_DELETION_REQUEST_STATUSES);

/**
 * The Role-management actions a governance policy can target (NEX-3199).
 *
 * 🚨 THIS IS THE WIRE VOCABULARY AND IT IS DELIBERATELY WIDER THAN THE DATABASE.
 * The Postgres enum holds exactly `CREATE_ROLE` and `DELETE_ROLE` — the two
 * structural actions, which carry an approval queue and are the only values a
 * `RoleManagementPolicy` row can hold. {@link APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS}
 * is that pair, and it is the set every settings surface reports on.
 *
 * `MANAGE_MEMBERS`, `MANAGE_GROUP_GRANTS` and `MANAGE_RESOURCES` name no live
 * decision anywhere: they were the vocabulary of an ORG-WIDE allow-list
 * (`RoleManagementGrant`) that gated ongoing per-item actions on an existing
 * Role, and that axis is retired and its table dropped. They stay HERE, on the
 * wire, so a caller naming one is answered by the WRITE with
 * `ROLE_MANAGEMENT_ACTION_HAS_NO_APPROVAL_QUEUE` and a sentence saying what is
 * wrong — rather than 404ing as an unmatched route, or failing a generic schema
 * validation that says nothing. Keeping them is what makes that refusal
 * expressible; it is not a mirror of the database and must not be "corrected"
 * into one.
 *
 * 🔴 THE ASYMMETRY THAT DECIDES WHAT BELONGS IN THE POSTGRES TYPE: adding a
 * label to it is cheap, and removing one is a full rewrite of
 * `RoleManagementPolicy.action` under `AccessExclusiveLock`, which blocks reads
 * as well as writes. So a value belongs in the database enum only once something
 * reads it, and a value that stops being read is cheap to leave there and
 * expensive to remove.
 *
 * The write invariant is pinned by
 * `roles/__tests__/role-management-policy-writes-are-queued-actions.spec.ts`,
 * which holds that only the two queued actions are ever WRITTEN or REPORTED —
 * a stronger claim than the type alone makes, and the reason the retired values
 * are safe to keep on the wire.
 */
export const RoleManagementAction = {
  CREATE_ROLE: "CREATE_ROLE",
  DELETE_ROLE: "DELETE_ROLE",
  MANAGE_MEMBERS: "MANAGE_MEMBERS",
  MANAGE_GROUP_GRANTS: "MANAGE_GROUP_GRANTS",
  MANAGE_RESOURCES: "MANAGE_RESOURCES"
} as const;

export type RoleManagementAction = (typeof RoleManagementAction)[keyof typeof RoleManagementAction];

export const ROLE_MANAGEMENT_ACTIONS = Object.values(RoleManagementAction) as [
  RoleManagementAction,
  ...RoleManagementAction[]
];

export const RoleManagementActionSchema = z.enum(ROLE_MANAGEMENT_ACTIONS);

/**
 * The actions a `RoleManagementPolicy` can actually govern: the two whose write
 * path can file a request for review instead of doing the thing.
 *
 * This is the population every governance settings surface reports, internal and
 * public alike. Reporting the other three would describe a control that controls
 * nothing — `requiresApproval` on `MANAGE_MEMBERS` is read by no code path, so a
 * caller who set it would be configuring an outcome that cannot occur.
 *
 * 🚨 IT IS A NARROWING OF THE ENUM, NEVER A SECOND VOCABULARY. A value here must
 * be a `RoleManagementAction`, and `ROLE_MANAGEMENT_ACTIONS` stays the full
 * five — the wire schema still validates against the enum, so an older client
 * sending a retired value is refused by the WRITE rather than accepted into a
 * table nothing reads.
 */
export const APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS = [
  RoleManagementAction.CREATE_ROLE,
  RoleManagementAction.DELETE_ROLE
] as const;

export type ApprovalQueuedRoleManagementAction =
  (typeof APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS)[number];

export const isApprovalQueuedRoleManagementAction = (
  action: RoleManagementAction
): action is ApprovalQueuedRoleManagementAction =>
  (APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS as readonly RoleManagementAction[]).includes(action);
